// src/lib/fetchWithRetry.js
import { logger } from './logger/index.js';
import { API_LOG_ENABLED } from './config.js';
import { getAccessToken } from './authToken.js';

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export async function fetchWithRetry(url, options = {}, maxRetries = 3, interval = 15000) {
  let lastError;
  const method = (options.method || 'GET').toUpperCase();

  const token = await getAccessToken();
  const headers = { ...(options.headers || {}) };
  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }
  const requestOptions = { ...options, headers };

  for (let i = 0; i < maxRetries; i++) {
    const attempt = i + 1;
    try {
      if (API_LOG_ENABLED) {
        const label = attempt === 1 ? '→' : `↺ retry ${attempt}`;
        logger.debug(`[API] ${label} ${method} ${url}`);
      }

      const response = await fetch(url, requestOptions);

      if (response.ok) {
        if (API_LOG_ENABLED) logger.debug(`[API] ✓ ${response.status} ${method} ${url}`);
        return await response.json();
      }

      // Erro de cliente (4xx) — não adianta tentar de novo
      if (response.status >= 400 && response.status < 500) {
        const errorData = await response.json().catch(() => ({}));
        const clientError = new Error(errorData.message || `Erro de cliente ${response.status}`);
        clientError.isFatal = true;
        logger.error(`[fetchWithRetry] Erro de cliente na tentativa ${attempt}.`, clientError);
        throw clientError;
      }

      // Erro de servidor (5xx)
      const serverError = new Error(`Erro de servidor: ${response.status} ${response.statusText || ''}`);
      logger.warn(`[fetchWithRetry] Falha na tentativa ${attempt} (HTTP ${response.status}).`, serverError);
      throw serverError;
    } catch (err) {
      lastError = err;

      if (err.isFatal) throw err;

      logger.warn(`[fetchWithRetry] Erro capturado na tentativa ${attempt}: ${err.message}`);
      logger.warn(`Tentativa ${attempt} falhou para ${url}. Tentando novamente em ${interval / 1000}s...`);

      if (i < maxRetries - 1) {
        await delay(interval);
      } else {
        logger.error(`[fetchWithRetry] Todas as ${maxRetries} tentativas esgotadas.`);
      }
    }
  }

  throw lastError;
}
